import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import log from 'loglevel';

import { useSnoozeEmail } from '../../../services/snoozedEmails.service';
import {
  useCommunicationIntentsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { useCustomToast } from '../../useCustomToast';

export const useSnoozeUserCommunication = () => {
  const { t } = useTranslation();
  const { snoozeEmail, loading: isSnoozing } = useSnoozeEmail();
  const { successMessage, errorMessage } = useCustomToast();
  const handleSnoozeUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      snoozeUntil: number,
      options?: { onSuccess?: () => void },
    ) => {
      const { archiveUserCommunication } =
        useUserCommunicationsStore.getState();
      const { setSnoozedIntent, clearIntents, settleIntent } =
        useCommunicationIntentsStore.getState();
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      messageIdsArray.forEach((messageId) => setSnoozedIntent({ messageId }));

      try {
        const results = await Promise.all(
          messageIdsArray.map(async (messageId) => {
            try {
              const created = await snoozeEmail({ messageId, snoozeUntil });
              if (created) {
                settleIntent(messageId);
              }
              return { messageId, success: !!created };
            } catch (error) {
              log.error('useSnoozeUserCommunication: snoozeEmail failed', {
                messageId,
                error,
              });
              return { messageId, success: false };
            }
          }),
        );

        const succeeded = results.filter((r) => r.success);
        const failed = results.filter((r) => !r.success);

        succeeded.forEach(({ messageId }) =>
          archiveUserCommunication(messageId),
        );
        // GET_SNOOZED_EMAILS has already refetched (awaitRefetchQueries) by the
        // time snoozeEmail resolves, so the durable snoozedMessageIds filter
        // already covers these — the intent can go now instead of waiting for
        // reconcileSnoozedIntents on the next render.
        clearIntents(succeeded.map((r) => r.messageId));

        if (failed.length > 0) {
          clearIntents(failed.map((r) => r.messageId));
        }

        const time = format(
          snoozeUntil * 1000,
          t('dateFormats.shortDateWithTime'),
        );

        if (failed.length === 0) {
          options?.onSuccess?.();
          successMessage(
            messageIdsArray.length === 1
              ? t('inbox.snooze.toast.success', { time })
              : t('inbox.snooze.toast.successMultiple', {
                  count: succeeded.length,
                  time,
                }),
          );
        } else if (succeeded.length > 0) {
          errorMessage(
            t('inbox.snooze.toast.partialError', {
              failed: failed.length,
              total: messageIdsArray.length,
            }),
          );
        } else {
          errorMessage(t('inbox.snooze.toast.error'));
        }
      } catch (error) {
        log.error('useSnoozeUserCommunication: unexpected failure', { error });
        clearIntents(messageIdsArray);
        errorMessage(t('inbox.snooze.toast.error'));
      }
    },
    [snoozeEmail, successMessage, errorMessage, t],
  );

  return { handleSnoozeUserCommunication, isSnoozing };
};
